import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    Button,
    CircularProgress,
    Paper,
    Divider,
    Select,
    MenuItem,
    InputLabel,
    Checkbox,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PetProfile } from '../types/onboarding';

interface IntakeFormProps {
    professionalName?: string;
    onSubmit: (data: Partial<PetProfile>) => Promise<void>;
    isSubmitting?: boolean;
    initialData?: Partial<PetProfile>;
    embedded?: boolean;
    onCancel?: () => void;
    submitButtonText?: string;
}

export const IntakeForm: React.FC<IntakeFormProps> = ({
    professionalName = 'PetSnap Professional',
    onSubmit,
    isSubmitting = false,
    initialData,
    embedded = false,
    onCancel,
    submitButtonText,
}) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState<Partial<PetProfile>>(() => ({
        name: initialData?.name || '',
        breed: initialData?.breed || '',
        age: initialData?.age,
        emergencyContact: initialData?.emergencyContact || '',
        vetContact: initialData?.vetContact || '',
        foodAllergies: {
            hasAllergies: initialData?.foodAllergies?.hasAllergies || false,
            details: initialData?.foodAllergies?.details || ''
        },
        freshTreatPermission: initialData?.freshTreatPermission || false,
        medicalStatus: {
            isSpayedNeutered: initialData?.medicalStatus?.isSpayedNeutered || false
        },
        pottyHabits: initialData?.pottyHabits || '',
        barkingHabits: initialData?.barkingHabits || '',
        boardingDates: {
            from: initialData?.boardingDates?.from || '',
            to: initialData?.boardingDates?.to || ''
        },
        chewingHabits: {
            hasHabit: initialData?.chewingHabits?.hasHabit || false,
            details: initialData?.chewingHabits?.details || ''
        },
        separationAnxiety: initialData?.separationAnxiety || '',
        favoriteThings: initialData?.favoriteThings || [],
        favoriteThingsOther: initialData?.favoriteThingsOther || '',
        walkingPreferences: initialData?.walkingPreferences || '',
        medications: initialData?.medications || '',
        specialNeeds: initialData?.specialNeeds || '',
        type: initialData?.type || 'dog',
        gender: initialData?.gender || 'unknown',
    }));

    useEffect(() => {
        if (initialData) {
            setFormData(prev => ({
                ...prev,
                ...initialData,
                foodAllergies: {
                    hasAllergies: initialData.foodAllergies?.hasAllergies ?? prev.foodAllergies?.hasAllergies ?? false,
                    details: initialData.foodAllergies?.details ?? prev.foodAllergies?.details ?? ''
                },
                medicalStatus: {
                    isSpayedNeutered: initialData.medicalStatus?.isSpayedNeutered ?? prev.medicalStatus?.isSpayedNeutered ?? false
                },
                boardingDates: {
                    from: initialData.boardingDates?.from ?? prev.boardingDates?.from ?? '',
                    to: initialData.boardingDates?.to ?? prev.boardingDates?.to ?? ''
                },
                chewingHabits: {
                    hasHabit: initialData.chewingHabits?.hasHabit ?? prev.chewingHabits?.hasHabit ?? false,
                    details: initialData.chewingHabits?.details ?? prev.chewingHabits?.details ?? ''
                },
            }));
        }
    }, [initialData]);

    const handleChange = (field: keyof PetProfile | string, value: any) => {
        if (field.includes('.')) {
            const parts = field.split('.');
            setFormData(prev => ({
                ...prev,
                [parts[0]]: {
                    ...(prev[parts[0] as keyof PetProfile] as any),
                    [parts[1]]: value
                }
            }));
        } else {
            setFormData(prev => ({ ...prev, [field]: value }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        await onSubmit(formData);
    };

    const formContent = (
        <form onSubmit={handleSubmit}>
            {/* 1. General Information */}
            <Box sx={{ mt: embedded ? 0 : 4, mb: 2 }}>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                    {t('intakeForm.sections.general')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <TextField
                        sx={{ flex: 1 }}
                        label={t('intakeForm.fields.petName')}
                        margin="normal"
                        required
                        value={formData.name || ''}
                        onChange={(e) => handleChange('name', e.target.value)}
                    />
                    <FormControl sx={{ flex: 1, mt: 2 }} required>
                        <InputLabel id="pet-type-label">{t('intakeForm.fields.petType')}</InputLabel>
                        <Select
                            labelId="pet-type-label"
                            value={formData.type || 'dog'}
                            label={t('intakeForm.fields.petType')}
                            onChange={(e) => handleChange('type', e.target.value)}
                        >
                            <MenuItem value="dog">{t('intakeForm.fields.dog')}</MenuItem>
                            <MenuItem value="cat">{t('intakeForm.fields.cat')}</MenuItem>
                            <MenuItem value="other">{t('intakeForm.fields.other')}</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <TextField
                        sx={{ flex: 1 }}
                        label={t('intakeForm.fields.breed')}
                        margin="normal"
                        fullWidth
                        value={formData.breed || ''}
                        onChange={(e) => handleChange('breed', e.target.value)}
                    />
                    <TextField
                        sx={{ flex: 1 }}
                        label={t('intakeForm.fields.age')}
                        type="number"
                        margin="normal"
                        fullWidth
                        value={formData.age !== undefined && formData.age !== null ? formData.age : ''}
                        onChange={(e) => handleChange('age', e.target.value === '' ? undefined : Number(e.target.value))}
                    />
                    <FormControl sx={{ flex: 1, mt: 2 }} required>
                        <InputLabel id="pet-gender-label">{t('intakeForm.fields.gender')}</InputLabel>
                        <Select
                            labelId="pet-gender-label"
                            value={formData.gender || ''}
                            label={t('intakeForm.fields.gender')}
                            onChange={(e) => handleChange('gender', e.target.value)}
                        >
                            <MenuItem value="male">{t('intakeForm.fields.male')}</MenuItem>
                            <MenuItem value="female">{t('intakeForm.fields.female')}</MenuItem>
                            <MenuItem value="unknown">{t('intakeForm.fields.unknown')}</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
                <TextField
                    fullWidth
                    label={t('intakeForm.fields.emergencyContact')}
                    margin="normal"
                    required
                    value={formData.emergencyContact || ''}
                    onChange={(e) => handleChange('emergencyContact', e.target.value)}
                />
                <TextField
                    fullWidth
                    label={t('intakeForm.fields.vetContact')}
                    margin="normal"
                    value={formData.vetContact || ''}
                    onChange={(e) => handleChange('vetContact', e.target.value)}
                />
                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                    <TextField
                        fullWidth
                        label={t('intakeForm.fields.boardingDateFrom')}
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={formData.boardingDates?.from || ''}
                        onChange={(e) => handleChange('boardingDates.from', e.target.value)}
                    />
                    <TextField
                        fullWidth
                        label={t('intakeForm.fields.boardingDateTo')}
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={formData.boardingDates?.to || ''}
                        onChange={(e) => handleChange('boardingDates.to', e.target.value)}
                    />
                </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* 2. Health & Nutrition */}
            <Box sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                    {t('intakeForm.sections.health')}
                </Typography>

                <FormControl component="fieldset" margin="normal" fullWidth>
                    <FormLabel component="legend">
                        <strong>{t('intakeForm.fields.foodAllergiesLabel')}</strong> {t('intakeForm.fields.foodAllergiesDesc')}
                    </FormLabel>
                    <RadioGroup
                        row
                        value={formData.foodAllergies?.hasAllergies ? 'yes' : 'no'}
                        onChange={(e) => handleChange('foodAllergies.hasAllergies', e.target.value === 'yes')}
                    >
                        <FormControlLabel value="no" control={<Radio />} label={t('intakeForm.fields.no')} />
                        <FormControlLabel value="yes" control={<Radio />} label={t('intakeForm.fields.yes')} />
                    </RadioGroup>
                    {formData.foodAllergies?.hasAllergies && (
                        <TextField
                            fullWidth
                            label={t('intakeForm.fields.foodAllergiesDetails')}
                            margin="dense"
                            size="small"
                            value={formData.foodAllergies?.details || ''}
                            onChange={(e) => handleChange('foodAllergies.details', e.target.value)}
                        />
                    )}
                </FormControl>

                <FormControl component="fieldset" margin="normal" fullWidth sx={{ mt: 2 }}>
                    <FormLabel component="legend">
                        <strong>{t('intakeForm.fields.freshTreatLabel')}</strong> {t('intakeForm.fields.freshTreatDesc')}
                    </FormLabel>
                    <RadioGroup
                        row
                        value={formData.freshTreatPermission ? 'yes' : 'no'}
                        onChange={(e) => handleChange('freshTreatPermission', e.target.value === 'yes')}
                    >
                        <FormControlLabel value="yes" control={<Radio />} label={t('intakeForm.fields.yes')} />
                        <FormControlLabel value="no" control={<Radio />} label={t('intakeForm.fields.no')} />
                    </RadioGroup>
                </FormControl>

                <FormControl component="fieldset" margin="normal" fullWidth sx={{ mt: 2 }}>
                    <FormLabel component="legend">
                        <strong>{t('intakeForm.fields.medicalStatusLabel')}</strong> {t('intakeForm.fields.medicalStatusDesc')}
                    </FormLabel>
                    <RadioGroup
                        row
                        value={formData.medicalStatus?.isSpayedNeutered ? 'yes' : 'no'}
                        onChange={(e) => handleChange('medicalStatus.isSpayedNeutered', e.target.value === 'yes')}
                    >
                        <FormControlLabel value="yes" control={<Radio />} label={t('intakeForm.fields.yes')} />
                        <FormControlLabel value="no" control={<Radio />} label={t('intakeForm.fields.no')} />
                    </RadioGroup>
                </FormControl>

                <TextField
                    fullWidth
                    label={t('intakeForm.fields.medicationsLabel')}
                    helperText={t('intakeForm.fields.medicationsDesc')}
                    margin="normal"
                    multiline
                    rows={3}
                    value={formData.medications || ''}
                    onChange={(e) => handleChange('medications', e.target.value)}
                />
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* 3. Behavior & Daily Habits */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                    {t('intakeForm.sections.behavior')}
                </Typography>

                <TextField
                    fullWidth
                    label={t('intakeForm.fields.pottyHabits')}
                    margin="normal"
                    multiline
                    rows={2}
                    value={formData.pottyHabits || ''}
                    onChange={(e) => handleChange('pottyHabits', e.target.value)}
                />

                <TextField
                    fullWidth
                    label={t('intakeForm.fields.barkingHabits')}
                    margin="normal"
                    multiline
                    rows={2}
                    value={formData.barkingHabits || ''}
                    onChange={(e) => handleChange('barkingHabits', e.target.value)}
                />

                <FormControl component="fieldset" margin="normal" fullWidth sx={{ mt: 2 }}>
                    <FormLabel component="legend">
                        <strong>{t('intakeForm.fields.chewingHabitsLabel')}</strong> {t('intakeForm.fields.chewingHabitsDesc')}
                    </FormLabel>
                    <RadioGroup
                        row
                        value={formData.chewingHabits?.hasHabit ? 'yes' : 'no'}
                        onChange={(e) => handleChange('chewingHabits.hasHabit', e.target.value === 'yes')}
                    >
                        <FormControlLabel value="no" control={<Radio />} label={t('intakeForm.fields.no')} />
                        <FormControlLabel value="yes" control={<Radio />} label={t('intakeForm.fields.yes')} />
                    </RadioGroup>
                    {formData.chewingHabits?.hasHabit && (
                        <TextField
                            fullWidth
                            label={t('intakeForm.fields.chewingHabitsDetails')}
                            margin="dense"
                            size="small"
                            value={formData.chewingHabits?.details || ''}
                            onChange={(e) => handleChange('chewingHabits.details', e.target.value)}
                        />
                    )}
                </FormControl>

                <TextField
                    fullWidth
                    label={t('intakeForm.fields.separationAnxiety')}
                    margin="normal"
                    multiline
                    rows={2}
                    value={formData.separationAnxiety || ''}
                    onChange={(e) => handleChange('separationAnxiety', e.target.value)}
                />
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* 4. Favorites & Recreation */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                    {t('intakeForm.sections.favorites')}
                </Typography>

                <FormControl component="fieldset" margin="normal" fullWidth>
                    <FormLabel component="legend">
                        <strong>{t('intakeForm.fields.favoriteThingsLabel')}</strong>
                    </FormLabel>
                    {[
                        t('intakeForm.fields.favoriteThings.balls'),
                        t('intakeForm.fields.favoriteThings.bones'),
                        t('intakeForm.fields.favoriteThings.plush'),
                        t('intakeForm.fields.favoriteThings.other'),
                    ].map((item) => (
                        <FormControlLabel
                            key={item}
                            control={
                                <Checkbox
                                    checked={formData.favoriteThings?.includes(item) || false}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        const currentFavorites = formData.favoriteThings || [];
                                        if (e.target.checked) {
                                            handleChange('favoriteThings', [...currentFavorites, item]);
                                        } else {
                                            handleChange('favoriteThings', currentFavorites.filter((f) => f !== item));
                                        }
                                    }}
                                />
                            }
                            label={item}
                        />
                    ))}
                </FormControl>

                {formData.favoriteThings?.includes(t('intakeForm.fields.favoriteThings.other')) && (
                    <TextField
                        fullWidth
                        label={t('intakeForm.fields.favoriteThingsOtherDetails')}
                        margin="normal"
                        size="small"
                        value={formData.favoriteThingsOther || ''}
                        onChange={(e) => handleChange('favoriteThingsOther', e.target.value)}
                    />
                )}

                <TextField
                    fullWidth
                    label={t('intakeForm.fields.walkingPreferences')}
                    margin="normal"
                    multiline
                    rows={2}
                    value={formData.walkingPreferences || ''}
                    onChange={(e) => handleChange('walkingPreferences', e.target.value)}
                />
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* 5. Special Instructions */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                    {t('intakeForm.sections.special')}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    {t('intakeForm.fields.specialInstructionsDesc')}
                </Typography>

                <TextField
                    fullWidth
                    label={t('intakeForm.fields.specialNeeds')}
                    margin="normal"
                    multiline
                    rows={3}
                    value={formData.specialNeeds || ''}
                    onChange={(e) => handleChange('specialNeeds', e.target.value)}
                />
            </Box>

            {embedded ? (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
                    {onCancel && (
                        <Button
                            variant="outlined"
                            color="inherit"
                            onClick={onCancel}
                            disabled={isSubmitting}
                        >
                            {t('common.cancel') || 'Cancel'}
                        </Button>
                    )}
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? <CircularProgress size={24} color="inherit" /> : (submitButtonText || t('common.save') || 'Save')}
                    </Button>
                </Box>
            ) : (
                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="large"
                    fullWidth
                    disabled={isSubmitting}
                    sx={{ py: 1.5 }}
                >
                    {isSubmitting ? <CircularProgress size={24} color="inherit" /> : t('intakeForm.fields.submit')}
                </Button>
            )}
        </form>
    );

    if (embedded) {
        return <Box>{formContent}</Box>;
    }

    return (
        <Paper elevation={3} sx={{ p: 4, maxWidth: 800, mx: 'auto', my: 4 }}>
            <Typography variant="h4" gutterBottom fontWeight="bold" color="primary">
                {t('intakeForm.title')}
            </Typography>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                {professionalName}
            </Typography>
            <Typography variant="body1" paragraph color="text.secondary">
                {t('intakeForm.description')}
            </Typography>
            {formContent}
        </Paper>
    );
};
